import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

/**
 * Dados do portal do corretor da Azos (migration 288): o que a corretora
 * recebe, lido com o login dela pela function seguradoras-azos-portal. O
 * `resumo` vem pronto da function (_shared/seguradoras/azos/portal.ts → mapear).
 */

export type ConexaoPortal = {
  crm_user_id: number;
  email: string;
  situacao: "conectado" | "erro" | "login_expirado";
  erro: string | null;
  corretora: string | null;
  susep: string | null;
  lido_em: string | null;
  resumo: ResumoPortal;
};

export type ResumoPortal = {
  sinistros?: { abertos: number | null; rascunhos: number | null } | null;
  leads?: { total: number | null; a_contatar: number | null } | null;
  proximo_pagamento?: { data: string | null; valor: number | null; situacao: "agendado" | "em_verificacao" } | null;
  extrato?: {
    desde: string | null;
    comissao: number | null;
    campanha: number | null;
    total: number | null;
    meses: { mes: string; comissao: number | null; campanha: number | null }[];
  } | null;
  campanhas?: {
    azos_mais?: {
      nivel: string | null;
      proximo_nivel: string | null;
      premio_ativo: number | null;
      premio_proximo_nivel: number | null;
      persistencia: number | null;
      proxima_apuracao: string | null;
    } | null;
    passaporte?: {
      pontos: number | null;
      meta_pontos: number | null;
      classificado: boolean | null;
      persistencia: number | null;
    } | null;
    multiplicazos?: {
      multiplicador: number | null;
      proximo_multiplicador: number | null;
      premio_mes: number | null;
      falta_proximo: number | null;
      bonus_mes: number | null;
      apolices_mes: number | null;
    } | null;
  };
};

export type ComissaoPortal = {
  id: number;
  pago_em: string | null;
  segurado: string | null;
  fatura_em: string | null;
  parcela: number | null;
  valor_fatura: number | null;
  situacao: string | null;
  competencia: string | null;
  percentual: number | null;
  valor_comissao: number | null;
};

export type RecebimentoPortal = {
  azos_id: string;
  mes_referencia: string | null;
  tipo: string | null;
  pago_por: string | null;
  valor_bruto: number | null;
  imposto: number | null;
  valor_liquido: number | null;
  nf_enviada: boolean | null;
  /** Como o portal: enviada, reportado ou pendente (migration 305; null em leitura antiga). */
  nf_situacao: "enviada" | "reportado" | "pendente" | null;
};

export type CotacaoPortal = {
  azos_id: string;
  segurado: string | null;
  valor: number | null;
  valor_desconto: number | null;
  situacao: string | null;
  situacao_texto: string | null;
  atualizada_em: string | null;
  coberturas: { codigo: string | null; capital: number | null }[];
};

const NIVEIS: Record<string, string> = {
  EMPTY: "Sem nível",
  BRONZE: "Bronze",
  PRATA: "Prata",
  OURO: "Ouro",
  PARTNER: "Partner",
};

/** Nível do Azos+ como aparece no portal. */
export const rotuloNivel = (n: string | null | undefined) => (n ? (NIVEIS[n] ?? n) : "—");

const SITUACOES: Record<string, string> = { paid: "Paga", pending: "Pendente", canceled: "Estornada", cancelled: "Estornada" };

/** Situação da parcela de comissão. */
export const rotuloSituacao = (s: string | null | undefined) => (s ? (SITUACOES[s] ?? s) : "—");

/** Em aberto primeiro; dentro de cada grupo, a mais recente antes. */
export const ordenarCotacoes = (lista: CotacaoPortal[]) =>
  [...lista].sort(
    (a, b) =>
      Number(b.situacao === "open") - Number(a.situacao === "open") ||
      (b.atualizada_em ?? "").localeCompare(a.atualizada_em ?? ""),
  );

/** 0.25 → "25%"; 0.2375 → "23,8%". */
export const percentual = (p: number | null | undefined) =>
  p == null ? "—" : `${(p * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

/** "2026-10-27" → "27/10/2026" (sem fuso: a data já é do dia). */
export const dataCurta = (d: string | null | undefined) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");

/** "2026-09-01" → "set/2026". */
export const mesCurto = (d: string | null | undefined) => {
  if (!d) return "—";
  const [ano, mes] = d.split("-").map(Number);
  return `${["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"][mes - 1]}/${ano}`;
};

/** Progresso 0–100 de valor sobre meta. */
export const progresso = (valor: number | null | undefined, meta: number | null | undefined) =>
  valor == null || !meta ? 0 : Math.max(0, Math.min(100, Math.round((valor / meta) * 100)));

/** Lê em páginas de 1000 (o PostgREST corta em 1000 sem erro). */
async function paginado<T>(tabela: string, select: string, crmUserId: number, ordem: string[]): Promise<T[]> {
  const linhas: T[] = [];
  for (let de = 0; de < 50_000; de += 1000) {
    let q = supabase.from(tabela as never).select(select).eq("crm_user_id", crmUserId);
    for (const c of ordem) q = q.order(c, { ascending: false, nullsFirst: false });
    const { data, error } = await q.range(de, de + 999);
    if (error) throw new Error(error.message);
    linhas.push(...((data ?? []) as T[]));
    if (!data || data.length < 1000) break;
  }
  return linhas;
}

export const lerConexoes = async (): Promise<ConexaoPortal[]> => {
  const { data, error } = await supabase
    .from("azos_portal_conexoes" as never)
    .select("crm_user_id, email, situacao, erro, corretora, susep, lido_em, resumo")
    .order("corretora");
  if (error) throw new Error(error.message);
  return (data ?? []) as ConexaoPortal[];
};

export const lerComissoes = (crmUserId: number) =>
  paginado<ComissaoPortal>(
    "azos_portal_comissoes",
    "id, pago_em, segurado, fatura_em, parcela, valor_fatura, situacao, competencia, percentual, valor_comissao",
    crmUserId,
    ["pago_em", "id"],
  );

/** Situação da nota fiscal, como o portal mostra. */
export const situacaoNf = (r: Pick<RecebimentoPortal, "nf_enviada" | "nf_situacao">) =>
  r.nf_situacao ?? (r.nf_enviada ? "enviada" : "pendente");

export const ROTULO_NF = { enviada: "Enviada", reportado: "Reportada", pendente: "Pendente" } as const;

export const lerRecebimentos = (crmUserId: number) =>
  paginado<RecebimentoPortal>(
    "azos_portal_recebimentos",
    "azos_id, mes_referencia, tipo, pago_por, valor_bruto, imposto, valor_liquido, nf_enviada, nf_situacao",
    crmUserId,
    ["mes_referencia", "azos_id"],
  );

export const lerCotacoes = (crmUserId: number) =>
  paginado<CotacaoPortal>(
    "azos_portal_cotacoes",
    "azos_id, segurado, valor, valor_desconto, situacao, situacao_texto, atualizada_em, coberturas",
    crmUserId,
    ["atualizada_em", "azos_id"],
  );

/**
 * Usuário com corretora vinculada (seguros_corretores.user_id, migrations 206 e
 * 298): vê a carteira dela em Seguros. Conectar o portal faz o vínculo.
 */
export const useCorretoraVinculada = (habilitado: boolean) =>
  useQuery({
    queryKey: ["seguros", "corretora-vinculada"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("seguros_tenho_corretora" as never);
      if (error) throw new Error(error.message);
      return data === true;
    },
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
